'use client'

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useEffect, useState } from "react"
import { Skeleton } from "@/components/ui/skeleton"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { AlertCircle, History, MessageCircleMore, UserRoundPlus, Users, Coins } from "lucide-react"
import { PageContainer } from "@/components/page-container"
import { InfoTooltip } from "@/app/(home)/profile/_components/info-tooltip"
import { StatTile } from "@/app/(home)/profile/_components/stat-tile"
import { ReferralLinkCard, type InviteLink } from "@/app/(home)/profile/_components/referral-link-card"
import { ReferralStatsDetailsDialog } from "@/app/(home)/profile/_components/referral-stats-details-dialog"
import { useAccount, useReferralsStats } from "@/hooks/queries"
import { NEXT_PUBLIC_DOMAIN } from "@/common/core/constants"
import { brand } from "@/config/brand"
import { cn, formatMoney } from "@/lib/utils"
import Cookies from 'js-cookie'
import OperationsHistoryModal from '@/app/(home)/profile/_components/OperationsHistoryModal'

interface UserData {
  user: {
    verified: boolean;
    balance: number;
    register_type: number;
    invite: {
      id: number;
      user_id: number;
      issuer_id: number;
      benefit: {
        description: string;
        free_period_for_referral: number;
        free_period_for_user: number;
        accruals_for_user_percents: number;
      };
      is_active: boolean;
      type: string;
    };
    count_invited_users: number;
    id: number;
    email: string;
    username: string;
    first_name: string;
    created_at: string;
  };
  flags: {
    need_account_update: boolean;
    need_password_update: boolean;
    need_email_verification: boolean;
    ban?: {
      message: string;
      issuer_name: string;
      issuer_id: number;
      support: boolean;
      date: string;
    };
    _has_password: boolean;
  };
  invite_links: InviteLink[];
}

export default function Profile() {
  // Данные аккаунта из общего кэша — возврат на /profile теперь мгновенный,
  // /profile/edit переиспользует тот же результат.
  const { data, isLoading } = useAccount()
  const userInfo = data as UserData | undefined
  const { data: referralsStats, isLoading: isReferralsStatsLoading } = useReferralsStats()
  const [showOperationsHistory, setShowOperationsHistory] = useState(false)

  useEffect(() => {
    const hasSession = Cookies.get('refreshToken') || Cookies.get('accessToken')
    if (!hasSession) {
      window.location.href = '/auth/login'
    }
  }, [])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950">
        <PageContainer className="py-6 lg:py-10">
          <div className="flex items-center gap-4 mb-8 lg:mb-10">
            <Skeleton className="h-12 w-12 lg:h-14 lg:w-14 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
          </div>
          <Skeleton className="h-[360px] w-full rounded-2xl mb-8 lg:mb-10" />
          <Skeleton className="h-[140px] w-full rounded-xl" />
        </PageContainer>
      </div>
    )
  }

  if (!userInfo) {
    return (
      <div className="min-h-screen bg-gray-950">
        <PageContainer className="py-6 lg:py-10">Ошибка загрузки данных</PageContainer>
      </div>
    )
  }

  const domain = NEXT_PUBLIC_DOMAIN
  const links = userInfo.invite_links ?? []

  return (
    <div className="min-h-screen bg-gray-950">
      <PageContainer className="py-6 lg:py-10">

          {/* Профиль + баланс в одной шапке — акцент страницы на реферальной программе ниже */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 lg:mb-10 animate-in fade-in-0 slide-in-from-top-2 duration-500">
            <div className="flex items-center gap-4 min-w-0">
              <Avatar className="h-12 w-12 lg:h-14 lg:w-14 ring-2 ring-gray-800 shrink-0">
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-lg font-semibold">
                  {(userInfo.user.first_name || userInfo.user.username || "?").charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-100 truncate">
                  Привет, {userInfo.user.first_name || userInfo.user.username}
                </h1>
                <p className="text-gray-400 text-sm truncate">
                  {userInfo.user.email}
                </p>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-1 mb-0.5">
                <p className="text-gray-400 text-xs">Баланс</p>
                <InfoTooltip text="Баланс — сумма, доступная к выводу прямо сейчас. Можно выводить не всё сразу, а частями. Деньги не поступают на карту напрямую — оформите списание в чате с поддержкой ниже." />
              </div>
              <p className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-green-400 to-blue-400 text-transparent bg-clip-text leading-none">
                {formatMoney(userInfo.user.balance)} ₽
              </p>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowOperationsHistory(true);
                }}
                className="text-gray-500 hover:text-gray-400 text-xs transition-colors flex items-center gap-1 mt-1.5"
                type="button"
              >
                <History className="w-3 h-3" />
                Управление подпиской
              </button>
            </div>
          </div>

          {/* Поддержка — самостоятельный блок, не относится к реферальной программе ниже */}
          <Card
            onClick={() => window.open(brand.social.telegramSupportBot, '_blank', 'noopener,noreferrer')}
            className="mb-6 lg:mb-8 cursor-pointer border-blue-500/30 bg-blue-500/5 backdrop-blur-sm transition-all duration-300 hover:border-blue-400/50 hover:bg-blue-500/10 animate-in fade-in-0 slide-in-from-top-2 duration-500"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-lg lg:text-xl text-gray-100">
                <MessageCircleMore className="w-6 h-6 lg:w-7 lg:h-7 text-blue-400 shrink-0" />
                <span>
                  Открыть чат по вопросам партнерской программы
                  <span className="block text-xs lg:text-sm font-normal text-gray-400 mt-1">
                    Специалист поддержки ответит на вопросы и оформит выплату в Telegram
                  </span>
                </span>
              </CardTitle>
            </CardHeader>
          </Card>

          {/* Предупреждения — состояние аккаунта важнее промо-блоков ниже */}
          {userInfo.flags.need_account_update && (
            <Card className="mb-6 bg-yellow-500/10 border-yellow-500/20 backdrop-blur-sm">
              <CardContent className="flex items-center gap-3 py-4 lg:py-5">
                <AlertCircle className="w-5 h-5 lg:w-6 lg:h-6 text-yellow-500 shrink-0" />
                <p className="text-yellow-500 text-base lg:text-lg">Требуется обновление данных аккаунта</p>
              </CardContent>
            </Card>
          )}

          {userInfo.flags.ban && (
            <Card className="mb-6 bg-red-500/10 border-red-500/20 backdrop-blur-sm">
              <CardContent className="flex items-center gap-3 py-4 lg:py-5">
                <AlertCircle className="w-5 h-5 lg:w-6 lg:h-6 text-red-500 shrink-0" />
                <div>
                  <p className="text-red-500 font-semibold text-base lg:text-lg mb-1">Аккаунт заблокирован</p>
                  <p className="text-red-400 text-sm lg:text-base">{userInfo.flags.ban.message}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Реферальная программа — смысловой центр страницы */}
          <section
            className="relative mb-8 lg:mb-10 overflow-hidden rounded-2xl border border-purple-500/20 bg-gray-900 p-4 lg:p-10 animate-in fade-in-0 slide-in-from-bottom-4 duration-700 fill-mode-both"
            style={{ animationDelay: '100ms' }}
          >
            <div className="relative">
              <h2 className="text-2xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text mb-2">
                Реферальная программа
              </h2>
              <p className="text-gray-400 mb-6 lg:mb-8">
                Делись ссылкой и получай бонусы за друзей
              </p>

              <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4 mb-3 p-3 sm:p-4 lg:p-5 rounded-xl bg-black/20 border border-white/5">
                <StatTile
                  icon={<UserRoundPlus className="w-5 h-5 text-purple-400" />}
                  value={userInfo.user.count_invited_users}
                  label="Приглашено"
                  plain
                  size="md"
                />
                <StatTile
                  icon={<Users className="w-5 h-5 text-purple-400" />}
                  value={isReferralsStatsLoading ? <Skeleton className="h-8 w-10 mx-auto" /> : referralsStats?.registrations_count ?? '—'}
                  label="Регистраций"
                  plain
                  size="md"
                />
                <StatTile
                  icon={<Coins className="w-5 h-5 text-purple-400" />}
                  value={isReferralsStatsLoading ? <Skeleton className="h-8 w-16 mx-auto" /> : referralsStats ? `${formatMoney(referralsStats.total_earned)} ₽` : '—'}
                  label="Заработано с рефералов"
                  plain
                  size="md"
                />
              </div>

              <div className="flex justify-center mb-8 lg:mb-10">
                <ReferralStatsDetailsDialog stats={referralsStats} />
              </div>

              {links.length > 0 ? (
                <div className={cn("grid gap-4", links.length > 1 && "sm:grid-cols-2")}>
                  {links.map((link, i) => (
                    <div
                      key={link.id}
                      className="min-w-0 animate-in fade-in-0 slide-in-from-bottom-2 duration-500 fill-mode-both"
                      style={{ animationDelay: `${250 + i * 100}ms` }}
                    >
                      <ReferralLinkCard link={link} domain={domain} index={i} featured={i === 0} showQr={i === 0} wide={links.length === 1} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400">Реферальные ссылки пока не созданы</p>
              )}
            </div>
          </section>

      </PageContainer>

      {showOperationsHistory && (
        <OperationsHistoryModal
          isOpen={showOperationsHistory}
          onClose={() => {
            setShowOperationsHistory(false);
          }}
          userId={userInfo.user.id}
        />
      )}
    </div>
  )
}
